





























































import { getUserData } from "@/auth/getUserData";
import useMessagingFilters from "@/composables/useMessagingFilters";
import useMessagingStatusFilters from "@/composables/v2.1/useMessagingStatusFilters";
import useStoredMessages from "@/composables/v2.1/useStoredMessages";
import useUnreadChats from "@/composables/v2.1/useUnreadChats";
import toastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import i18n from "@/libs/i18n";
import { useChannelsStore } from "@/store/useChannelsStore";
import { useChatsStore } from "@/store/useChatsStore";
import { useContactsStore } from "@/store/useContactsStore";
import { useCustomersStore } from "@/store/useCustomersStore";
import { useTeamsStore } from "@/store/useTeamsStore";
import { useUsersStore } from "@/store/useUsersStore";
import { decrypt } from "@/utils/crypto";
import ChatListChat from "@/views/modules/messaging-inbox/chat-list/components/chatListChat.vue";
import ChatsListContextMenu from "@/views/modules/messaging-inbox/chat-list/components/chatsListContextMenu.vue";
import { chatListPs, scrollToTopInChatList } from "@/views/modules/messaging-inbox/useChat";
import useChatsListContextMenu from "@/views/modules/messaging-inbox/useChatsListContextMenu";
import useTypingIndicators from "@/views/modules/messaging-inbox/useTypingIndicators";
import {
    BaseWebsocketEvent,
    ChatsListChat,
    WebsocketChatUpdateEvent,
    WebsocketNewChatEvent,
    WebsocketNewEntriesEvent,
} from "@saysimple-platform/dtos";
import { computed, defineComponent, onUpdated, watch } from "@vue/composition-api";
import { BSkeleton } from "bootstrap-vue";
import Vue from "vue";
import { useToast } from "vue-toastification/composition";
import { ToastComponent } from "vue-toastification/dist/types/src/types";
import { isDebugEnabled } from "@/utils/v3/debug.utils";

export default defineComponent({
    components: {
        ChatListChat,
        BSkeleton,
        ChatsListContextMenu,
    },
    emits: ["open-chat"],
    sockets: {
        // @see https://justinternet.atlassian.net/wiki/spaces/SAYS/pages/647004161/Frontend+events
        socketEvent(
            this: Vue & { toast: (ToastComponent) => ToastComponent },
            socketMessage: { event: string; iv: string }
        ) {
            const chatsStore = useChatsStore();
            const wsMessage: BaseWebsocketEvent = decrypt(socketMessage.event, socketMessage.iv);

            // @NOTICE all those websocket events should only do anything with the chats list.
            let chatsListChat, payload, messageEntry, unreadChat;

            const { messaging } = useUnreadChats();

            switch (wsMessage.type) {
                case "NEW_ENTRY":
                    payload = wsMessage.payload as WebsocketNewEntriesEvent;

                    // ONLY if the entry is of specific type, it's actually a MESSAGE from A to B or other way around.
                    messageEntry = payload.entries.find((entry) =>
                        ["text", "attachment", "location"].includes(entry.type)
                    );
                    if (!messageEntry) {
                        return;
                    }

                    // - We set this new message as "last message" in the chats list AS LONG
                    chatsListChat = chatsStore.chatsList.find((chat) => chat.id === payload.chat.id);
                    if (!chatsListChat) {
                        return;
                    }

                    // @note from backend, the chat is always unread (logically) and have unreadMessages, so we set it to true directly if message belongs to active chat.
                    if (chatsStore.activeChat?.chat.id !== payload.chat.id) {
                        // But we can already have an unreadMessages count in the frontend, so we like to INCREASE that.
                        chatsListChat.unreadMessages++;
                        payload.chat.unreadMessages = chatsListChat.unreadMessages;
                    }

                    unreadChat = messaging.value.unreadChats.find(
                        (unreadChat) => unreadChat.chatId === payload.chat.id
                    );

                    chatsStore.updateChat({
                        ...payload.chat,
                        read: !(unreadChat && unreadChat.isChatRead === false),
                    });

                    break;
                case "NEW_CHAT":
                    payload = wsMessage.payload as WebsocketNewChatEvent;

                    unreadChat = messaging.value.unreadChats.find(
                        (unreadChat) => unreadChat.chatId === payload.chat.id
                    );

                    chatsStore.addChat({
                        ...payload.chat,
                        contact: payload.contact,
                        read: !(unreadChat && unreadChat.isChatRead === false),
                    });

                    break;
                case "CHAT_UPDATE":
                    payload = wsMessage.payload as WebsocketChatUpdateEvent;

                    unreadChat = messaging.value.unreadChats.find(
                        (unreadChat) => unreadChat.chatId === payload.chat.id
                    );

                    // No matter the entry type, update chat chat contact so it assigns correctly, moves to correct status filter etc.
                    chatsStore.updateChat(
                        {
                            ...payload.chat,
                            read: !(unreadChat && unreadChat.isChatRead === false),
                        },
                        this.$socket.client.id === wsMessage.senderId
                    );

                    if (payload.chat.id === chatsStore.activeChat?.chat.id) {
                        this.toast({
                            component: toastificationContent,
                            props: {
                                title: i18n.t("entities.conversation.Conversation"),
                                text: i18n.t("entities.conversation.changed"),
                                icon: "check",
                                variant: "success",
                            },
                        });
                    }

                    break;
            }
        },
    },
    setup() {
        const chatsStore = useChatsStore();
        const contactsStore = useContactsStore();
        const channelsStore = useChannelsStore();

        const { messaging } = useUnreadChats();
        const { getActiveFilter: activeFilter } = useMessagingFilters();
        const { filterReadStatus } = useMessagingStatusFilters();

        onUpdated(() => {
            if (isDebugEnabled()) {
                console.log("waa chatslist ");
            }
        });

        //----------------------------------------------------------------------

        // If page changes, we like to scroll the container to the top.
        watch(
            () => chatsStore.chatsListPagination.page,
            () => scrollToTopInChatList()
        );

        const resolveReadStatus = (chat: ChatsListChat): ChatsListChat => {
            if (chat.status === "closed") {
                return chat;
            }

            const unreadChat = messaging.value.unreadChats.find((unreadChat) => unreadChat.chatId === chat.id);

            chat.read = !(unreadChat && unreadChat.isChatRead === false);

            return chat;
        };

        const filterStatusFn = (chat: ChatsListChat): ChatsListChat | [] => {
            if (chatsStore.chatsList.length === 0) {
                return [];
            }

            // If no status filters are applied, we always want to return the chat, because all chats should be listed
            if (!activeFilter.value.status) {
                return chat;
            }

            // @ts-ignore
            if (chat.status !== activeFilter.value.status.query.status) {
                return;
            }

            // @todo need to refactor filters, but this is for now the easiest way.
            // @ts-ignore
            if (activeFilter.value.status?.name === "new") {
                if (!chat.assignedUserId && !chat.assignedTeamId) {
                    return chat;
                }
                // @ts-ignore
            } else if (activeFilter.value.status?.name === "team") {
                const { customerSettings } = useCustomersStore();
                if (customerSettings.expandedTeamFilter && chat.assignedTeamId) {
                    return chat;
                }

                if (chat.assignedTeamId && !chat.assignedUserId) {
                    return chat;
                }
                // @ts-ignore
            } else if (activeFilter.value.status?.name === "personal") {
                if (chat.assignedUserId === getUserData().id) {
                    return chat;
                }
                // @ts-ignore
            } else if (activeFilter.value.status?.name === "open") {
                if (chat.assignedUserId || (chat.assignedTeamId && chat.assignedUserId)) {
                    return chat;
                }
                // @ts-ignore
            } else if (activeFilter.value.status?.name === "archive") {
                if (chat.status === "closed") {
                    return chat;
                }
            }
        };

        //----------------------------------------------------------------------

        // If we toggle VIP, we should recalculate the score to have the chats list reactively up-to-date
        const resolvePriority = (chat: ChatsListChat): ChatsListChat => {
            // contact priority is at least in the 1000-range. Note that it can also be 1010 (depending on other conditions)
            if (chat.contact?.isHighPriority && chat.score < 1000) {
                chat.score += 1000;
            } else if (!chat.contact?.isHighPriority && chat.score > 999) {
                chat.score -= 1000;
            }

            return chat;
        };

        //----------------------------------------------------------------------

        const filteredChatsList = computed((): ChatsListChat[] => {
            if (chatsStore.chatsList.length === 0) {
                return [];
            }

            const chatList = chatsStore.chatsList
                .filter(filterStatusFn)
                .map(resolvePriority)
                .map(resolveReadStatus)
                .filter(filterReadStatus);

            // we do a reduce to prevent the doublons
            const idSet = new Set();
            return chatList.filter(({ id }) => {
                if (!idSet.has(id)) {
                    idSet.add(id);
                    return true;
                }
                return false;
            });
        });

        //----------------------------------------------------------------------

        const { storedMessages } = useStoredMessages();
        const { typingIndicators } = useTypingIndicators();

        const toast = useToast();
        const teamsStore = useTeamsStore();
        const usersStore = useUsersStore();

        const { ctxMenu } = useChatsListContextMenu();
        return {
            channelsStore,
            chatListPs,
            chatsStore,
            contactsStore,
            ctxMenu,
            filteredChatsList,
            storedMessages,
            teamsStore,
            toast,
            typingIndicators,
            usersStore,
        };
    },
});
